// noinspection JSUnusedGlobalSymbols

'use strict';

import {trigger} from './events';
import {mutate} from './promise-fastdom';
import promiseRAF from './promise-raf';
import WaitForTransition from './WaitForTransition';

const _ = new WeakMap();

export default function ToggleArea(area, name = 'nav', behaviors = []) {

    const o = _.set(this, {
        area: area,
        name: name,
        behaviors: behaviors,
        opened: false,
        active: false,
        pending: [],
        running: null,
    }).get(this);

    o.ready = Promise.all(behaviors.map(b => b.init(this)))
        .then(mutate(() => {
            o.active = o.opened = this.isActive();
            o.area.hidden = !o.active;
            this.toggleClass('on', o.active);
            this.toggleClass('ready');
            trigger.call(o.area, o.name + '/init', this);
        }))
        .then(() => this);

    o.wft = new WaitForTransition((() => {
        let ctrl = area;
        const ctrlSelector = area.getAttribute('data-ctrl');
        if (ctrlSelector) {
            ctrl = area.querySelector(ctrlSelector);
            if (!ctrl) {
                throw new Error(`ToggleArea: controller element "${ctrlSelector}" not found in area.`);
            }
        }
        return ctrl;
    })());

    // true to open, false to close
    o.run = (mode) => {
        const o = _.get(this);
        o.pending = () => {
            const suffix = mode ? 'Open' : 'Close';
            if (o.opened === mode) {
                return;
            }
            o.opened = !o.opened;
            o.running = Promise.resolve()
                .then(() => trigger.call(o.area, `${o.name}/before-${mode ? 'open' : 'close'}`, this))
                .then(() => Promise.all(o.behaviors.map(b => b[`before${suffix}`].call(b, this))))
                .then(mutate(() => {
                    this.toggleClass(mode ? 'opening' : 'closing');
                    mode && (o.area.hidden = false);
                }))
                .then(promiseRAF())
                .then(() => Promise.all(o.behaviors.map(b => b[`start${suffix}`].call(b, this))))
                .then(mutate(() => this.toggleClass('on', mode)))
                .then(() => o.wft.wait())
                .then(mutate(() => {
                    mode || (o.area.hidden = true);
                    this.toggleClass(mode ? 'opening' : 'closing', false);
                }))
                .then(() => Promise.all(o.behaviors.map(b => b[`after${suffix}`].call(b, this))))
                .then(() => trigger.call(o.area, `${o.name}/after-${mode ? 'open' : 'close'}`, this))
                .catch(console.error);
        };

        // execute pending task
        Promise.resolve(o.running).then(() => {
            const o = _.get(this);
            if (!o.pending) {
                return;
            }
            o.pending.call();
            o.pending = null;
        });
    };
}

ToggleArea.prototype.update = function () {
    const o = _.get(this);
    const active = this.isActive();
    o.active !== active && o.run(active);
    o.active = active;
};

ToggleArea.prototype.open = function () {
    _.get(this).run(true);
};

ToggleArea.prototype.close = function () {
    _.get(this).run(false);
};

// should be called within a `mutate` callback
ToggleArea.prototype.toggleClass = function (classes, toggle = true) {
    const o = _.get(this);
    classes.split(' ').forEach(className => {
        o.area.classList.toggle(o.name + '-' + className, toggle);
        o.behaviors.forEach(b => b.toggleClass(o.name + '-' + className, toggle));
    });
};

ToggleArea.prototype.isActive = function () {
    for (const b of _.get(this).behaviors) {
        if (b.active) {
            return true;
        }
    }
    return false;
};

ToggleArea.prototype.ready = function () {
    return _.get(this).ready;
};
ToggleArea.prototype.area = function () {
    return _.get(this).area;
};
ToggleArea.prototype.name = function () {
    return _.get(this).name;
};
ToggleArea.prototype.opened = function () {
    return _.get(this).opened;
};

ToggleArea.prototype.destroy = function () {
    const o = _.get(this);
    return Promise.resolve()
        .then(mutate(() => {
            this.toggleClass('opening closing on ready', false);
            o.area.hidden = false;
        }))
        .then(() => Promise.all(o.behaviors.map(b => b.destroy(this))))
        .then(() => _.delete(this));
};

export function Behavior() {
    this.active = false;
}

Behavior.prototype.init = function () {
};
Behavior.prototype.toggleClass = function () {
};
Behavior.prototype.beforeOpen = function () {
};
Behavior.prototype.startOpen = function () {
};
Behavior.prototype.afterOpen = function () {
};
Behavior.prototype.beforeClose = function () {
};
Behavior.prototype.startClose = function () {
};
Behavior.prototype.afterClose = function () {
};
Behavior.prototype.destroy = function () {
};
